import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Grid, Paper, styled, Typography } from '@mui/material';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import ImageLoader from 'js/components/common/ImageLoader';
import Attributes from 'js/components/property/attribute/Attributes';
import MapListingSummary from 'js/components/searchResults/views/map/listings/MapListingSummary';
import EventFlags from 'js/components/searchResults/views/EventFlags';
import Colors from 'js/constants/colors';
import PropertyAttrFormatter from 'js/helpers/PropertyAttrFormatter';
import { useSearchSelectContext } from 'js/components/higherOrderComponents/search/SearchResultsWithItemSelection';
import Checkbox from 'js/components/common/Checkbox';
import { trackEvent } from '../../../../actions/segment';
import Segment from '../../../../helpers/Segment';
import SEGMENT from '../../../../constants/segment';
import { routeCodes } from '../../../../constants/routes';

export const Container = styled(Paper)({
    overflow: 'hidden',
    borderRadius: '8px',
    '.simplebar-wrap__custom-scroll': {
        '.simplebar-content': {
            marginRight: 0,
        },
    },
});
const NoDataContainer = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '101px',
});
const StyledImageContainer = styled('div')({
    borderTopLeftRadius: '6px',
    borderTopRightRadius: '6px',
    display: 'flex',
    width: '150px',
    height: '101px',
});

const BubbleContent = (props) => {
    const { itemSelected, toggleSelection } = useSearchSelectContext();
    const statusType = useSelector(state => state.searchResults.get('statusType'));
    const dispatch = useDispatch();

    if (props.noData) {
        return (
            <NoDataContainer>
                <Typography variant="body1">Sorry, no property found at this location.</Typography>
            </NoDataContainer>
        );
    }

    if (props.error) {
        return (
            <NoDataContainer>
                <Typography variant="body1">Sorry, there was an error fetching the property data.</Typography>
            </NoDataContainer>
        );
    }
    const handleCheckboxClick = (event) => {
        event.stopPropagation();
        if (event.target.checked) {
            dispatch(trackEvent(Segment.selectProperty({
                page: SEGMENT.EVENT_PROPERTIES.SEARCH,
                searchType: props.searchType,
                searchTab: props.searchTab,
                resultView: 'Map',
                objectContext: 'Bubble',
            })));
        }
        toggleSelection(props.id);
    };

    return (
        <Grid
            container
            direction="row"
            sx={{
                '& + &': { borderTop: `1px solid ${Colors.GRAY_BORDER}` }
            }}
            // disable closing of bubble when you click inside
            onClick={e => e.stopPropagation()}
        >
            <Grid item xs={4}>
                <Box
                    sx={{
                        '.search-select': {
                            position: 'absolute',
                            margin: '8px',
                        }
                    }}
                >
                    {!props.hideSelectCheckbox && <Checkbox
                        checked={itemSelected?.includes(props.id)}
                        onChange={handleCheckboxClick}
                        id={`property-bubble-map-view-search-${props.id}`}
                        customClass="search-select"
                    />}
                    <StyledImageContainer>
                        <ImageLoader
                            src={props.image?.src}
                            alt={props.address}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'left',
                            }}
                            prospectHeight={101}
                            isThumbnail
                            fitNoImage
                        />
                    </StyledImageContainer>
                </Box>
            </Grid>
            <Grid item container xs={8} direction="column" sx={{ padding: '10px 10px 10px 0', justifyContent: 'space-between' }}>
                <Grid item>
                    <Box
                        sx={{
                            maxWidth: '94%',
                        }}
                    >
                        <Link
                            className="address"
                            to={routeCodes.PROPERTY.path(props.address, props.id)}
                            target="_blank"
                        >
                            <Typography
                                sx={{
                                    ':hover': {
                                        textDecoration: 'underline',
                                        color: Colors.PRIMARY.MAIN,
                                    },
                                }}
                                variant="subtitle2"
                                color="secondary"
                            >
                                { props.address }
                            </Typography>
                        </Link>
                    </Box>
                </Grid>
                <Grid item sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    '.MuiChip-root': {
                        height: '18px',
                        minWidth: '18px',
                    },
                    'ul .property-attribute .property-attribute-val': {
                        padding: 0,
                    },
                    '.list-inline': {
                        'img': {
                            width: '20px',
                        },
                    },
                    '.property-attribute': {
                        '&[type="bed"], &[type="car"], &[type="bath"]': {
                            width: '30px',
                        }
                    },
                }}>
                    <Attributes
                        attributes={props.attributes}
                        disableFloorArea
                    />
                    <EventFlags
                        isAgentAdvice={props.saleData.isAgentsAdvice}
                        isRecentSale={props.flags.isRecentSale}
                        isForSaleCampaign={props.flags.isForSaleCampaign}
                        isForRentCampaign={props.flags.isForRentCampaign}
                        isHistoric={props.flags.isHistoric}
                        icon
                    />
                </Grid>
                <Grid item sx={{
                    '.MuiTypography-root': {
                        color: Colors.TEXT_PRIMARY,
                        maxWidth: '313px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                }}>
                    <MapListingSummary
                        propertyId={props.id}
                        saleData={props.saleData}
                        state={props.state}
                        statusType={statusType}
                        listingPrice={PropertyAttrFormatter.formatPriceDescription(props.saleCampaign?.lastListedPrice)}
                        listingDate={moment(props.saleCampaign?.endDate).format('DD MMM YYYY')}
                        rentalPrice={PropertyAttrFormatter.formatRentalPrice(props.rentData?.price, props.rentData?.period)}
                        rentalDate={moment(props.rentData?.date).format('DD MMM YYYY')}
                        sx={{
                            fontWeight: 400,
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0.15px',
                            color: Colors.BLACK_87_OPAQUE,
                        }}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

BubbleContent.propTypes = {
    image: PropTypes.shape({
        src: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.arrayOf(PropTypes.string),
        ]),
    }),
    address: PropTypes.string,
    state: PropTypes.string,
    noData: PropTypes.bool,
    error: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    attributes: PropTypes.shape({
        landArea: PropTypes.number,
        beds: PropTypes.number,
        carSpaces: PropTypes.number,
        baths: PropTypes.number,
    }),
    saleData: PropTypes.shape({
        type: PropTypes.string,
        price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        settlementDate: PropTypes.string,
        contractDate: PropTypes.string,
        isPriceWithheld: PropTypes.bool,
        isAgentsAdvice: PropTypes.bool,
    }),
    saleCampaign: PropTypes.shape({
        lastListedPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        endDate: PropTypes.string,
    }),
    rentData: PropTypes.shape({
        price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        period: PropTypes.string,
        date: PropTypes.string,
    }),
    flags: PropTypes.shape({
        isRecentSale: PropTypes.bool,
        isForSaleCampaign: PropTypes.bool,
        isForRentCampaign: PropTypes.bool,
        isHistoric: PropTypes.bool,
    }),
    searchType: PropTypes.string,
    searchTab: PropTypes.string,
    hideSelectCheckbox: PropTypes.bool,
}

export default BubbleContent;
